import { SITE_ROUTES } from "@/config/siteRoutes";
import { CmsManagedPage } from "@/pages/EditorialPages/CmsManagedPage";
import type { CmsContentPageData } from "@/pages/shared/cmsContent";

const fallback: CmsContentPageData = {
  id: 4,
  slug: "our-identity",
  title: "Our Identity",
  intro: "Strategy, creativity and thoughtful execution brought together through one clear direction.",
  seoTitle: "Our Identity",
  searchDescription:
    "Strategy, creativity and thoughtful execution brought together through one clear direction.",
  url: SITE_ROUTES.identity,
  bodyHtml: "",
  paragraphs: [],
  sections: [
    {
      type: "text",
      heading: "Our Story",
      bodyHtml:
        "<p>VenueX was created to bring strategy, creativity and coordination together under one trusted partner.</p>" +
        "<p>We shape brands, digital experiences, events and tailored business solutions with a clear understanding of each client\u2019s goals. From the first idea to final delivery, every detail is approached with consistency, purpose and care.</p>" +
        "<p>Our work is not defined by a single service, but by our ability to connect every touchpoint into one coherent and memorable experience.</p>",
    },
    {
      type: "profile",
      heading: "Meet the Founder",
      photoUrl: "",
      photoAlt: "Xenia Kimitzioglou",
      name: "Xenia Kimitzioglou",
      role: "Founder & Managing Director, VenueX",
      bodyHtml:
        "<p>Xenia Kimitzioglou founded VenueX with a clear vision: to transform ideas and different business needs into thoughtful, practical and carefully executed solutions.</p>" +
        "<p>With experience in business coordination, marketing, corporate communication and event management, her approach combines strategic thinking, creative direction and hands-on coordination.</p>" +
        "<p>From brand identity and digital presence to corporate events, exhibitions and destination weddings, Xenia leads every collaboration with clarity, consistency and personal attention.</p>" +
        "<p>Her philosophy is simple: listen carefully, understand the real need and create solutions that deliver lasting value.</p>",
      buttonLabel: "Connect on LinkedIn",
      buttonUrl: "https://www.linkedin.com/in/xenia-kimitzioglou-97292072/",
    },
  ],
};

const identityPrinciples = [
  {
    html: "<p><strong>01 — STRATEGY BEFORE AESTHETICS</strong><br />We begin with purpose, audience and direction, creating a clear foundation for every decision that follows.</p>",
    text: "01 — STRATEGY BEFORE AESTHETICS. We begin with purpose, audience and direction, creating a clear foundation for every decision that follows.",
  },
  {
    html: "<p><strong>02 — CONSISTENCY AT EVERY TOUCHPOINT</strong><br />From identity and website to communication and events, every element works together as one cohesive brand experience.</p>",
    text: "02 — CONSISTENCY AT EVERY TOUCHPOINT. From identity and website to communication and events, every element works together as one cohesive brand experience.",
  },
  {
    html: "<p><strong>03 — IDEAS BROUGHT TO LIFE</strong><br />We move from concept to execution with clarity, attention to detail and responsibility at every stage.</p>",
    text: "03 — IDEAS BROUGHT TO LIFE. We move from concept to execution with clarity, attention to detail and responsibility at every stage.",
  },
] as const;

export function OurIdentityPage() {
  return (
    <CmsManagedPage
      slug="our-identity"
      currentPath={SITE_ROUTES.identity}
      eyebrow="Who we are"
      visualImage="/static/preview/venuex-logo-768x432.png"
      visualAlt="VenueX logo mark"
      visualFit="contain"
      leadTitle="WHERE IDENTITY BECOMES EXPERIENCE"
      leadCopy="VenueX brings brand identity, digital presence and live experience together under one clear and cohesive direction."
      routeLinks={[
        { href: SITE_ROUTES.mission, label: "Discover our mission", meta: "OUR APPROACH" },
        { href: SITE_ROUTES.servicesArchive, label: "Explore our services", meta: "WHAT WE DO" },
        { href: SITE_ROUTES.portfolio, label: "View our portfolio", meta: "SELECTED WORK" },
      ]}
      primaryCtaLabel="EXPLORE SERVICES"
      primaryCtaHref={SITE_ROUTES.servicesArchive}
      secondaryCtaLabel="VIEW PORTFOLIO"
      secondaryCtaHref={SITE_ROUTES.portfolio}
      hideHeroIntro
      connectedTitle="EXPLORE VENUEX"
      connectedCopy="Discover our approach, capabilities and selected work."
      servicePanelTitle={"More than a name.\nA way of working."}
      servicePanelNote=""
      servicePanelBody="VenueX was created from the belief that a brand should feel consistent wherever it is experienced through its identity, digital presence, communication and live experiences. We combine strategy, creativity and execution to shape brands and experiences that feel distinctive, purposeful and complete."
      servicePanelAccent="Strategically led. Thoughtfully delivered."
      hideServiceNumber
      featureTitle="WHAT DEFINES VENUEX"
      featureCopy="Three principles guide every project we take on."
      featureParagraphs={identityPrinciples}
      hideFeatureIndex
      hideConnectedIntro
      fallback={fallback}
    />
  );
}
